/* number.control_adapter.js */

"use strict";

const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const util = require("util");
const q = require("q");
const InputNumberControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputNumber;
const StyleUtil = require("../StyleUtil");

/**
 * @event NumberControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */
/**
 * @event NumberControlAdapter#InitializationComplete
 * @description This event is fired when the control has completely Initialized.
 * @type {Object}
 * @property initializedValue {string} The initialized "starting" value
 */

/**
 * @class NumberControlAdapter
 * @classdesc exposes the gl_textfield as a Form Control for entry of numeric values.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires NumberControlAdapter#ValueChanged
 */
module.exports = function NumberAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    //call Event Emitter Constructor
    EventEmitter.call(this);

    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
    //The name of the control
    this.name = formCtrlDef.name;
    var _scope;

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        var val = formCtrlDef.value;
        if (_scope.numberApi && _scope.numberApi.getValue) {
            val = _scope.numberApi.getValue();
        }
        return val;
    };

    /**
     * Set this controls current value.
     * @param val {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (val) {
        _scope.numberApi.setValue(val);
    };

    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    /**
     * Returns if the number control value is valid based on given validation config (min, max, onlyInteger, step)
     * @returns {boolean}
     */
    this.isInvalid = function () {
        return _scope.numberApi.isInvalid();
    };

    /**
     * Returns validation messages associated with underlying control based on given validation config (min, max, onlyInteger, step)
     * @returns {Array#string}
     */
    this.getValidationMessages = function () {
        return _scope.numberApi.validationMessages;
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {};

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope
     */

    this.$init = function ($scope, $timeout) {
        _scope = $scope;

        // Apply item layout styles - these styles customize how this element is placed inside parent element
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        StyleUtil.appendClassListToElement(
            $scope.jTypeContext.layout.inlineLabels,
            containingElem[0]
        );

        // an isEmpty check on an int returns true, so we need to add this also to a isObject check.
        if (_.isObject(formCtrlDef.value) && _.isEmpty(formCtrlDef.value)) {
            formCtrlDef.value = null;
        }

        if (_.isUndefined(formCtrlDef.value)) {
            formCtrlDef.value = null;
        }

        //Setup API object
        $scope.numberApi = new InputNumberControl({
            name: formCtrlDef.name,
            label: formCtrlDef.displayInfo.label,
            value: formCtrlDef.value,
            placeholder: formCtrlDef.displayInfo.placeholder,
            title: formCtrlDef.displayInfo.tooltip,
        });
        containingElem.append($scope.numberApi.renderer.rootElement);
        $scope.numberApi.on("Value", function (newVal, oldVal) {
            if (newVal !== oldVal) {
                self.emit("ValueChanged", {
                    name: formCtrlDef.name,
                    containingElem: containingElem,
                    controlAdapter: self,
                    formControlDefinition: formCtrlDef,
                    oldValue: oldVal,
                    newValue: newVal,
                });
            }
        });
        $scope.numberApi.on("InvalidUserInput", function (newVal, oldVal) {
            if (newVal !== oldVal) {
                self.emit("Invalid", {
                    name: formCtrlDef.name,
                    containingElem: containingElem,
                    controlAdapter: self,
                    formControlDefinition: formCtrlDef,
                    oldValue: oldVal,
                    newValue: newVal,
                });
            }
        });

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });

        var loadDefered = q.defer();
        $timeout(function () {
            loadDefered.resolve();
        });
        return loadDefered.promise;
    };

    this.$compiledAndLinked = function (/*$scope, $timeout*/) {
        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
            this.setReadOnly(true);
        }

        // If this is multiEditMode, set the field as read only by default
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }

        this._required = _.get(formCtrlDef, "validationInfo.required", false);
        this._required = this._required !== false ? true : false; // takes care of bang  notation of required property in jtype
        _scope.numberApi.setRequired(this._required);
        _scope.numberApi.setMin(_.get(formCtrlDef.configSettings, "Number.min", undefined));
        _scope.numberApi.setMax(_.get(formCtrlDef.configSettings, "Number.max", undefined));
        _scope.numberApi.setOnlyInteger(
            _.get(formCtrlDef.configSettings, "Number.onlyInteger", undefined)
        );
        _scope.numberApi.setStep(_.get(formCtrlDef.configSettings, "Number.step", undefined));
    };

    this.setReadOnly = function (flag) {
        _scope.numberApi.setDisabled(flag);
        if (_scope.numberApi && _scope.numberApi.setRequired && this._required) {
            _scope.numberApi.setRequired(!flag);
        }
    };

    /**
     * @type {Array.<String>}
     * @description $inject Annotation By adding the $inject property onto the init function the Angular
     * injection parameters can be specified.
     */
    this.$init.$inject = ["$scope", "$timeout"];
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
